import React, { useState, useEffect, useRef, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Loader2, Search, X } from "lucide-react";
import { workflowRepo } from "@/lib/api-services";
import { cn } from "@/lib/utils";

export interface SearchDropdownProps {
  type: "Workflows" | "Roles" | "Teams";
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function SearchDropdown({
  type,
  value = "",
  onChange,
  placeholder,
  disabled = false,
  className,
}: SearchDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [items, setItems] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchIdRef = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch items function
  const fetchPage = useCallback(
    async (pageToFetch: number, query: string, append: boolean = false) => {
      const currentFetchId = ++fetchIdRef.current;
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const res = await workflowRepo.searchEntities({
          type,
          name: query || undefined,
          page: pageToFetch,
          pageSize: 20,
        });

        if (currentFetchId !== fetchIdRef.current) return;

        if (append) {
          setItems((prev) => Array.from(new Set([...prev, ...res.items])));
        } else {
          setItems(Array.from(new Set(res.items)));
        }

        setHasMore(res.hasMore);
        setPage(pageToFetch);
      } catch (err) {
        console.error(`Error searching ${type}:`, err);
        if (currentFetchId === fetchIdRef.current) {
          if (!append) setItems([]);
          setHasMore(false);
        }
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [type]
  );

  // Initial load when dropdown is opened
  useEffect(() => {
    if (open) {
      if (items.length === 0) {
        fetchPage(1, debouncedQuery, false);
      }
    }
  }, [open, debouncedQuery, fetchPage, items.length]);

  // Refetch when debounced search query changes
  useEffect(() => {
    if (open) {
      fetchPage(1, debouncedQuery, false);
    }
  }, [debouncedQuery, open, fetchPage]);

  // Infinite scroll on reach bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      if (!isLoading && !isLoadingMore && hasMore) {
        fetchPage(page + 1, debouncedQuery, true);
      }
    }
  };

  const handleSelect = (selectedName: string) => {
    onChange(selectedName);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const defaultPlaceholder =
    placeholder ||
    (type === "Workflows"
      ? "Select Workflow Name"
      : type === "Roles"
      ? "Select Role Name"
      : "Select Team Name");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal text-left h-9 px-3 bg-white hover:bg-slate-50 transition-colors",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">{value || defaultPlaceholder}</span>
          <div className="flex items-center shrink-0 ml-2 space-x-1">
            {value && (
              <span
                role="button"
                tabIndex={0}
                className="p-0.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleClear}
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0 shadow-lg border rounded-md bg-white z-50"
        align="start"
      >
        {/* Search input with debounced query */}
        <div className="flex items-center border-b px-3 py-2 gap-2 bg-slate-50/50">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            className="flex h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={`Search ${type.toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="text-muted-foreground hover:text-foreground p-0.5 transition-colors"
              title="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Scrollable list with infinite scroll */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="max-h-60 overflow-y-auto p-1 divide-y divide-border/20"
        >
          {/* Option for All / Empty filter */}
          <div
            onClick={() => handleSelect("")}
            className={cn(
              "flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer hover:bg-slate-100 transition-colors",
              !value && "font-medium text-workfinity-600 bg-workfinity-50/60"
            )}
          >
            <span>All {type}</span>
            {!value && <Check className="h-4 w-4 text-workfinity-600 shrink-0" />}
          </div>

          {/* If the current value is not yet in the items list, display it */}
          {value && !items.includes(value) && (
            <div
              onClick={() => handleSelect(value)}
              className="flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer bg-workfinity-50/60 font-medium text-workfinity-600"
            >
              <span className="truncate">{value}</span>
              <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-2" />
            </div>
          )}

          {/* Items */}
          {items.map((item, idx) => (
            <div
              key={`${item}-${idx}`}
              onClick={() => handleSelect(item)}
              className={cn(
                "flex items-center justify-between px-3 py-2 text-sm rounded cursor-pointer hover:bg-slate-100 transition-colors",
                value === item && "font-medium text-workfinity-600 bg-workfinity-50/60"
              )}
            >
              <span className="truncate">{item}</span>
              {value === item && <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-2" />}
            </div>
          ))}

          {/* Initial loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-4 text-sm text-muted-foreground space-x-2">
              <Loader2 className="h-4 w-4 animate-spin text-workfinity-500" />
              <span>Searching {type.toLowerCase()}...</span>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && items.length === 0 && !value && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No {type.toLowerCase()} found
            </div>
          )}

          {/* Scroll loading indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-2 text-xs text-muted-foreground space-x-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500" />
              <span>Loading more...</span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
